import React, { useState, useRef, useEffect } from 'react';
import { 
  Users2,
  MessageSquare,
  Brain,
  Target,
  TrendingUp,
  DollarSign,
  FileText,
  Eye,
  Trash2,
  Edit,
  Send,
  Activity,
  BarChart3,
  RefreshCw,
  Zap
} from 'lucide-react';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import HelpButton from '../common/HelpButton';
import AIService from '../../services/aiService';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const AINegotiationSupport = () => {
  const [activeTab, setActiveTab] = useState('strategy');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [negotiationContext, setNegotiationContext] = useState('');
  const [currentOffer, setCurrentOffer] = useState('');
  const [targetOutcome, setTargetOutcome] = useState('');
  const [negotiationType, setNegotiationType] = useState('contract');
  const [opponentProfile, setOpponentProfile] = useState('');
  const [chatMessage, setChatMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([
    {
      id: 1,
      type: 'ai',
      message: 'Hello! I\'m your AI Negotiation Assistant. I can help you with negotiation strategies, tactics, and best practices. What would you like to know about negotiation?',
      timestamp: new Date().toISOString(),
      recommendations: [
        'Ask about specific negotiation scenarios',
        'Get advice on handling difficult negotiators',
        'Learn about win-win negotiation strategies',
        'Understand negotiation psychology and tactics'
      ]
    }
  ]);
  const [isChatting, setIsChatting] = useState(false);
  const [isTestMode] = useState(process.env.NODE_ENV === 'development');
  
  const chatEndRef = useRef(null);

  // Mock data for negotiation sessions
  const [negotiationSessions, setNegotiationSessions] = useState([
    {
      id: 'NEGO-001',
      sessionName: 'IT Services Contract Negotiation',
      negotiationType: 'Service Level Agreement',
      status: 'Active',
      progress: 75,
      value: '$2.5M',
      duration: '2 weeks',
      participants: ['John Smith', 'Sarah Johnson'],
      lastActivity: '2024-02-16T10:30:00Z',
      nextMeeting: '2024-02-18T14:00:00Z',
      keyIssues: [
        'Uptime requirements',
        'Support response time',
        'Penalty clauses'
      ],
      aiRecommendations: 12,
      successProbability: 85,
      riskLevel: 'Medium',
      isActive: true,
      tags: ['sla', 'it-services', 'active'],
      createdBy: 'AI Negotiation Assistant',
      department: 'Legal',
      isBookmarked: true,
      isStarred: false,
      viewCount: 45,
      downloadCount: 8,
      strategyScore: 88,
      opponentStrength: 'High',
      marketPosition: 'Strong'
    },
    {
      id: 'NEGO-002',
      sessionName: 'Software License Renewal',
      negotiationType: 'Software License',
      status: 'Planning',
      progress: 25,
      value: '$850K',
      duration: '1 week',
      participants: ['Mike Chen', 'Lisa Rodriguez'],
      lastActivity: '2024-02-16T11:15:00Z',
      nextMeeting: '2024-02-19T10:00:00Z',
      keyIssues: [
        'License terms',
        'Pricing structure',
        'Support levels'
      ],
      aiRecommendations: 8,
      successProbability: 70,
      riskLevel: 'Low',
      isActive: true,
      tags: ['software', 'license', 'planning'],
      createdBy: 'AI Negotiation Assistant',
      department: 'Legal',
      isBookmarked: false,
      isStarred: true,
      viewCount: 12,
      downloadCount: 0,
      strategyScore: 75,
      opponentStrength: 'Medium',
      marketPosition: 'Moderate'
    },
    {
      id: 'NEGO-003',
      sessionName: 'Asset Purchase Agreement',
      negotiationType: 'Purchase Agreement',
      status: 'Completed',
      progress: 100,
      value: '$1.2M',
      duration: '3 weeks',
      participants: ['David Wilson', 'Emma Brown'],
      lastActivity: '2024-02-15T16:45:00Z',
      nextMeeting: null,
      keyIssues: [
        'Asset valuation',
        'Payment terms',
        'Warranty coverage'
      ],
      aiRecommendations: 15,
      successProbability: 100,
      riskLevel: 'Low',
      isActive: false,
      tags: ['purchase', 'assets', 'completed'],
      createdBy: 'AI Negotiation Assistant',
      department: 'Legal',
      isBookmarked: false,
      isStarred: false,
      viewCount: 8,
      downloadCount: 3,
      strategyScore: 92,
      opponentStrength: 'Low',
      marketPosition: 'Strong'
    }
  ]);

  // Calculate statistics
  const stats = {
    totalSessions: negotiationSessions.length,
    activeSessions: negotiationSessions.filter(s => s.status === 'Active').length,
    completedSessions: negotiationSessions.filter(s => s.status === 'Completed').length,
    planningSessions: negotiationSessions.filter(s => s.status === 'Planning').length,
    averageSuccessRate: negotiationSessions.reduce((sum, s) => sum + s.successProbability, 0) / negotiationSessions.length,
    totalValue: negotiationSessions.reduce((sum, s) => sum + parseFloat(s.value.replace('$', '').replace('M', '').replace('K', '')), 0),
    averageStrategyScore: negotiationSessions.reduce((sum, s) => sum + s.strategyScore, 0) / negotiationSessions.length
  };

  const tabs = [
    { id: 'strategy', label: 'Strategy Analysis', icon: Target },
    { id: 'chat', label: 'AI Assistant', icon: MessageSquare },
    { id: 'sessions', label: 'Negotiation Sessions', icon: Users2 },
    { id: 'templates', label: 'Templates', icon: FileText },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 }
  ];

  // Test Feature Handler
  const handleTestFeature = () => {
    if (activeTab === 'strategy') {
      setNegotiationType('contract');
      setCurrentOffer('Initial offer: $500K for 2-year contract with standard SLA terms');
      setTargetOutcome('Target: $600K for 3-year contract with enhanced SLA and penalty clauses');
      setNegotiationContext('Negotiating IT services contract with TechCorp. They are known for aggressive pricing but flexible on terms. Our budget allows up to $650K. Key issues: uptime requirements, support response time, and penalty clauses for service failures.');
      setOpponentProfile('TechCorp is a mid-size IT company with 200+ employees. Their strengths: competitive pricing, good technical expertise. Weaknesses: limited enterprise experience, smaller support team. Negotiation style: direct, data-driven, willing to compromise on non-price terms.');
    } else if (activeTab === 'chat') {
      // Auto-send a test message to the AI assistant
      const testMessage = 'What are the best negotiation strategies for IT service contracts?';
      setChatMessage(testMessage);
      // Automatically send the message after a short delay
      setTimeout(() => {
        // Create a temporary message object to send
        const userMessage = {
          id: Date.now(),
          type: 'user',
          message: testMessage,
          timestamp: new Date().toISOString()
        };
        
        setChatHistory(prev => [...prev, userMessage]);
        setChatMessage('');
        setIsChatting(true);
        
        // Call the AI service
        AIService.getNegotiationAdvice(testMessage, {
          context: negotiationContext,
          negotiationType: negotiationType
        }).then(response => {
          //console.log('Test AI Response:', response);
          
          if (response.success) {
            const aiMessage = {
              id: Date.now() + 1,
              type: 'ai',
              message: response.data?.advice || response.advice || 'Test response received successfully!',
              timestamp: new Date().toISOString(),
              recommendations: response.data?.recommendations || response.recommendations || []
            };
            
            setChatHistory(prev => [...prev, aiMessage]);
          }
        }).catch(error => {
          console.error('Test AI Error:', error);
          const errorMessage = {
            id: Date.now() + 1,
            type: 'ai',
            message: 'Test message sent successfully! (Note: AI response may vary based on backend availability)',
            timestamp: new Date().toISOString(),
            recommendations: []
          };
          setChatHistory(prev => [...prev, errorMessage]);
        }).finally(() => {
          setIsChatting(false);
          scrollToBottom();
        });
      }, 500);
    } else if (activeTab === 'sessions') {
      // Auto-refresh sessions data
      //console.log('Refreshing negotiation sessions...');
    } else if (activeTab === 'analytics') {
      // Auto-refresh analytics
      //console.log('Refreshing analytics...');
    }
  };

  // Define columns for DataTable - Optimized for better layout
  const columns = [
    {
      key: 'sessionName',
      label: 'Negotiation Session',
      width: '300px',
      render: (value, row) => (
        <div>
          <div className="fw-medium" style={{ fontSize: '14px' }}>{value}</div>
          <small className="text-muted" style={{ fontSize: '11px' }}>
            {row.negotiationType} • {row.value} • {row.participants.length} participants
          </small>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      width: '130px',
      type: 'badge',
      badgeClass: (value) => {
        const statusClasses = {
          'Active': 'badge-success',
          'Planning': 'badge-warning',
          'Completed': 'badge-secondary'
        };
        return statusClasses[value] || 'badge-secondary';
      }
    },
    {
      key: 'progress',
      label: 'Progress',
      width: '120px',
      render: (progress) => (
        <div className="d-flex align-items-center gap-2">
          <div style={{
            width: '60px',
            height: '6px',
            backgroundColor: '#e5e7eb',
            borderRadius: '3px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${progress}%`,
              height: '100%',
              backgroundColor: progress >= 80 ? '#10b981' : progress >= 50 ? '#f59e0b' : '#ef4444',
              transition: 'width 0.3s ease'
            }} />
          </div>
          <span style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>
            {progress}%
          </span>
        </div>
      )
    },
    {
      key: 'successProbability',
      label: 'Success Rate',
      width: '120px',
      render: (probability) => {
        const getProbabilityColor = (prob) => {
          if (prob >= 80) return '#10b981';
          if (prob >= 60) return '#f59e0b';
          return '#ef4444';
        };
        
        return (
          <span style={{
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor: getProbabilityColor(probability) + '20',
            color: getProbabilityColor(probability)
          }}>
            {probability}%
          </span>
        );
      }
    },
    {
      key: 'lastActivity',
      label: 'Last Activity',
      width: '120px',
      render: (date) => new Date(date).toLocaleDateString()
    }
  ];

  // Handler functions
  const handleAnalyzeStrategy = async () => {
    if (!negotiationContext.trim()) return;
    
    setIsAnalyzing(true);
    try {
      const response = await AIService.analyzeNegotiationStrategy({
        context: negotiationContext,
        currentOffer: currentOffer,
        targetOutcome: targetOutcome,
        negotiationType: negotiationType,
        opponentProfile: opponentProfile
      });
      
      if (response.success) {
        // Add AI response to chat history
        const aiMessage = {
          id: Date.now(),
          type: 'ai',
          message: response.strategy,
          timestamp: new Date().toISOString(),
          recommendations: response.recommendations || []
        };
        
        setChatHistory(prev => [...prev, aiMessage]);
        scrollToBottom();
      }
    } catch (error) {
      console.error('Error analyzing strategy:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSendMessage = async () => {
    if (!chatMessage.trim()) return;
    
    const userMessage = {
      id: Date.now(),
      type: 'user',
      message: chatMessage,
      timestamp: new Date().toISOString()
    };
    
    setChatHistory(prev => [...prev, userMessage]);
    const currentMessage = chatMessage;
    setChatMessage('');
    setIsChatting(true);
    
    try {
      //console.log('Sending message to AI:', currentMessage);
      const response = await AIService.getNegotiationAdvice(currentMessage, {
        context: negotiationContext,
        negotiationType: negotiationType
      });
      
      //console.log('AI Response:', response);
      
      if (response.success) {
        const aiMessage = {
          id: Date.now() + 1,
          type: 'ai',
          message: response.data?.advice || response.advice || 'I apologize, but I encountered an issue processing your request. Please try again.',
          timestamp: new Date().toISOString(),
          recommendations: response.data?.recommendations || response.recommendations || []
        };
        
        setChatHistory(prev => [...prev, aiMessage]);
      } else {
        // Handle error response
        const errorMessage = {
          id: Date.now() + 1,
          type: 'ai',
          message: 'I apologize, but I encountered an issue processing your request. Please try again.',
          timestamp: new Date().toISOString(),
          recommendations: []
        };
        
        setChatHistory(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error('Error getting advice:', error);
      
      // Add error message to chat
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        message: `I apologize, but I'm having trouble connecting right now. Error: ${error.message}. Please try again later.`,
        timestamp: new Date().toISOString(),
        recommendations: []
      };
      
      setChatHistory(prev => [...prev, errorMessage]);
    } finally {
      setIsChatting(false);
      scrollToBottom();
    }
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const clearChatHistory = () => {
    setChatHistory([
      {
        id: 1,
        type: 'ai',
        message: 'Hello! I\'m your AI Negotiation Assistant. I can help you with negotiation strategies, tactics, and best practices. What would you like to know about negotiation?',
        timestamp: new Date().toISOString(),
        recommendations: [
          'Ask about specific negotiation scenarios',
          'Get advice on handling difficult negotiators',
          'Learn about win-win negotiation strategies',
          'Understand negotiation psychology and tactics'
        ]
      }
    ]);
  };

  const handleViewSession = (session) => {
    //console.log('Viewing session:', session.sessionName);
    // TODO: Implement view session modal
  };

  const handleEditSession = (session) => {
    //console.log('Editing session:', session.sessionName);
    // TODO: Implement edit session modal
  };

  const handleDeleteSession = (session) => {
    setSelectedItem(session);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      setNegotiationSessions(prev => prev.filter(session => session.id !== selectedItem.id));
      setShowDeleteModal(false);
      setSelectedItem(null);
    } catch (error) {
      console.error('Error deleting session:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="page-container">
      <PageBreadcrumb />
      <PageHeader 
        title="AI Negotiation Support"
        description="AI-powered negotiation assistance, strategy recommendations, and real-time guidance"
      />
      
      {/* Test Feature and Help Buttons */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', justifyContent: 'flex-end' }}>
        {isTestMode && (
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={handleTestFeature}
            title="Auto-fill form fields for testing"
          >
            <Zap size={16} className="me-1" />
            Test Feature
          </button>
        )}
        <HelpButton 
          helpFile="ai/negotiation-support.html"
          title="AI Negotiation Help"
          size={16}
        />
      </div>

      {/* Tabbed Navigation */}
      <div className="page-tabs">
        <div className="page-tabs-list">
          {tabs.map(tab => (
            <div key={tab.id} className="page-tab-item">
              <button
                className={`page-tab-link ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <tab.icon size={18} className="page-tab-icon" />
                {tab.label}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="page-stats-grid">
        <div className="page-stat-card">
          <div className="card h-100 page-kpi-card">
            <div className="card-body page-kpi-body">
              <div className="page-kpi-content">
                <div className="page-kpi-text">
                  <h3 className="page-kpi-value color-1">
                    {stats.activeSessions}
                  </h3>
                  <p className="page-kpi-label">
                    Active Sessions
                  </p>
                </div>
                <div className="page-kpi-icon-container color-1">
                  <Users2 size={24} className="page-kpi-icon color-1" />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="page-stat-card">
          <div className="card h-100 page-kpi-card">
            <div className="card-body page-kpi-body">
              <div className="page-kpi-content">
                <div className="page-kpi-text">
                  <h3 className="page-kpi-value color-2">
                    {Math.round(stats.averageSuccessRate)}%
                  </h3>
                  <p className="page-kpi-label">
                    Success Rate
                  </p>
                </div>
                <div className="page-kpi-icon-container color-2">
                  <TrendingUp size={24} className="page-kpi-icon color-2" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="page-stat-card">
          <div className="card h-100 page-kpi-card">
            <div className="card-body page-kpi-body">
              <div className="page-kpi-content">
                <div className="page-kpi-text">
                  <h3 className="page-kpi-value color-3">
                    ${stats.totalValue.toFixed(1)}M
                  </h3>
                  <p className="page-kpi-label">
                    Total Value
                  </p>
                </div>
                <div className="page-kpi-icon-container color-3">
                  <DollarSign size={24} className="page-kpi-icon color-3" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="page-stat-card">
          <div className="card h-100 page-kpi-card">
            <div className="card-body page-kpi-body">
              <div className="page-kpi-content">
                <div className="page-kpi-text">
                  <h3 className="page-kpi-value color-6">
                    {Math.round(stats.averageStrategyScore)}
                  </h3>
                  <p className="page-kpi-label">
                    Strategy Score
                  </p>
                </div>
                <div className="page-kpi-icon-container color-6">
                  <Target size={24} className="page-kpi-icon color-6" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="row">
        <div className="col-12" style={{ overflowX: 'hidden' }}>
          {/* Strategy Analysis Tab */}
          {activeTab === 'strategy' && (
            <div className="page-tab-content">
              <div className="card" style={{
                borderRadius: '16px',
                border: 'none',
                boxShadow: '0 8px 32px rgba(0, 123, 143, 0.1)',
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)'
              }}>
                <div className="card-header" style={{
                  background: 'var(--primary-color)',
                  color: 'white',
                  borderRadius: '16px 16px 0 0',
                  border: 'none',
                  padding: '20px 24px'
                }}>
                  <h5 className="mb-0 d-flex align-items-center gap-2">
                    <Target size={20} />
                    Negotiation Strategy Analysis
                  </h5>
                </div>
                <div className="card-body p-4">
            
                  {/* Input Fields */}
                  <div className="row mb-4">
                    <div className="col-md-4">
                      <div className="form-group">
                        <label className="form-label required">
                          Negotiation Type
                        </label>
                        <select
                          className="form-control"
                          value={negotiationType}
                          onChange={(e) => setNegotiationType(e.target.value)}
                        >
                          <option value="contract">Contract Negotiation</option>
                          <option value="pricing">Pricing Negotiation</option>
                          <option value="partnership">Partnership Agreement</option>
                          <option value="employment">Employment Terms</option>
                          <option value="purchase">Purchase Agreement</option>
                        </select>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-group">
                        <label className="form-label required">
                          Current Offer
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={currentOffer}
                          onChange={(e) => setCurrentOffer(e.target.value)}
                          placeholder="Enter current offer details..."
                        />
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-group">
                        <label className="form-label required">
                          Target Outcome
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={targetOutcome}
                          onChange={(e) => setTargetOutcome(e.target.value)}
                          placeholder="Describe your target outcome..."
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row mb-4">
                    <div className="col-12">
                      <div className="form-group">
                        <label className="form-label required">
                          Negotiation Context
                        </label>
                        <textarea
                          className="form-control"
                          value={negotiationContext}
                          onChange={(e) => setNegotiationContext(e.target.value)}
                          placeholder="Describe the negotiation context, key issues, and background information..."
                          rows={4}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row mb-4">
                    <div className="col-12">
                      <div className="form-group">
                        <label className="form-label">
                          Opponent Profile
                        </label>
                        <textarea
                          className="form-control"
                          value={opponentProfile}
                          onChange={(e) => setOpponentProfile(e.target.value)}
                          placeholder="Describe the opponent's profile, strengths, weaknesses, and negotiation style..."
                          rows={3}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Analyze Button */}
                  <div className="d-flex justify-content-center">
                    <button
                      className="btn btn-primary btn-lg"
                      onClick={handleAnalyzeStrategy}
                      disabled={!negotiationContext.trim() || isAnalyzing}
                    >
                      {isAnalyzing ? (
                        <>
                          <RefreshCw size={20} className="me-2" style={{ animation: 'spin 1s linear infinite' }} />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Brain size={20} className="me-2" />
                          Analyze Strategy
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* AI Assistant Chat Tab */}
          {activeTab === 'chat' && (
            <div className="page-tab-content">
              <div className="card" style={{
                borderRadius: '16px',
                border: 'none',
                boxShadow: '0 8px 32px rgba(0, 123, 143, 0.1)',
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                height: '600px',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <div className="card-header" style={{
                  background: 'var(--primary-color)',
                  color: 'white',
                  borderRadius: '16px 16px 0 0',
                  border: 'none',
                  padding: '20px 24px'
                }}>
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0 d-flex align-items-center gap-2">
                      <MessageSquare size={20} />
                      AI Negotiation Assistant
                    </h5>
                    {chatHistory.length > 1 && (
                      <button
                        className="btn btn-sm btn-outline-light"
                        onClick={clearChatHistory}
                        title="Clear chat history"
                      >
                        Clear Chat
                      </button>
                    )}
                  </div>
                </div>
                <div className="card-body p-4" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            
                  {/* Chat Messages */}
                  <div className="flex-grow-1 overflow-auto p-3 bg-light rounded mb-3" style={{ minHeight: '400px' }}>
                    {chatHistory.length === 0 ? (
                      <div className="text-center text-muted py-5">
                        <MessageSquare size={48} className="mb-3" />
                        <h5>Start a conversation with the AI negotiation assistant...</h5>
                        <p>Ask questions about negotiation strategies, tactics, and best practices.</p>
                        
                        {/* Quick Action Buttons */}
                        <div className="mt-4">
                          <h6 className="mb-3">Quick Questions:</h6>
                          <div className="d-flex flex-wrap gap-2 justify-content-center">
                            <button 
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => setChatMessage('What are the best negotiation strategies for IT service contracts?')}
                            >
                              IT Contract Strategies
                            </button>
                            <button 
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => setChatMessage('How do I handle difficult negotiators?')}
                            >
                              Difficult Negotiators
                            </button>
                            <button 
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => setChatMessage('What are win-win negotiation techniques?')}
                            >
                              Win-Win Techniques
                            </button>
                            <button 
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => setChatMessage('How do I prepare for a negotiation?')}
                            >
                              Preparation Tips
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      chatHistory.map((message) => (
                        <div
                          key={message.id}
                          className={`d-flex mb-3 ${message.type === 'user' ? 'justify-content-end' : 'justify-content-start'}`}
                        >
                          <div className={`p-3 rounded ${message.type === 'user' ? 'bg-primary text-white' : 'bg-white border'}`} style={{ maxWidth: '70%' }}>
                            <div className="mb-2">{message.message}</div>
                            {message.recommendations && message.recommendations.length > 0 && (
                              <div className="mt-3">
                                <div className="fw-bold mb-2">Recommendations:</div>
                                <ul className="mb-0 ps-3">
                                  {message.recommendations.map((rec, index) => (
                                    <li key={index} className="mb-1">{rec}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                    {isChatting && (
                      <div className="d-flex justify-content-start mb-3">
                        <div className="p-3 bg-white border rounded d-flex align-items-center gap-2">
                          <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                          AI is thinking...
                        </div>
                      </div>
                    )}
                    <div ref={chatEndRef} />
                  </div>
                  
                  {/* Chat Input */}
                  <div className="d-flex gap-3 align-items-end">
                    <div className="flex-grow-1">
                      <textarea
                        className="form-control"
                        value={chatMessage}
                        onChange={(e) => setChatMessage(e.target.value)}
                        placeholder="Ask the AI assistant for negotiation advice..."
                        rows={2}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                        disabled={isChatting}
                      />
                      <small className="text-muted">
                        Press Enter to send, Shift+Enter for new line
                      </small>
                    </div>
                    <button
                      className="btn btn-primary"
                      onClick={handleSendMessage}
                      disabled={!chatMessage.trim() || isChatting}
                    >
                      {isChatting ? (
                        <>
                          <RefreshCw size={16} className="me-1" style={{ animation: 'spin 1s linear infinite' }} />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send size={16} className="me-1" />
                          Send
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Negotiation Sessions Tab */}
          {activeTab === 'sessions' && (
            <div className="page-tab-content">
              <div className="page-table-container">
                <DataTable
                  data={negotiationSessions}
                  columns={columns}
                  title="Negotiation Sessions"
                  searchable={true}
                  sortable={true}
                  exportable={true}
                  pagination={true}
                  pageSize={10}
                  showActions={true}
                  showCheckboxes={false}
                  onView={handleViewSession}
                  onEdit={handleEditSession}
                  onDelete={handleDeleteSession}
                  searchPlaceholder="Search sessions by name, type, or participants..."
                  loading={false}
                  emptyMessage="No negotiation sessions found. Start a new negotiation to get started."
                />
              </div>
            </div>
          )}

          {/* Other Tabs */}
          {activeTab === 'templates' && (
            <div className="page-tab-content">
              <div className="card">
                <div className="card-body text-center py-5">
                  <FileText size={64} className="text-muted mb-3" />
                  <h4 className="text-muted">Negotiation Templates</h4>
                  <p className="text-muted">Coming Soon! Pre-built negotiation templates and strategies.</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="page-tab-content">
              <div className="card">
                <div className="card-body text-center py-5">
                  <BarChart3 size={64} className="text-muted mb-3" />
                  <h4 className="text-muted">Negotiation Analytics</h4>
                  <p className="text-muted">Coming Soon! Detailed analytics and performance insights.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Negotiation Session"
        message={`Are you sure you want to delete "${selectedItem?.sessionName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeleting}
      />

      <style jsx="true">{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AINegotiationSupport;
